// IsomorphicService per ADR 010 — pure, read-only. The single buyer's-premium
// calculator: admin's editor preview, bidder's bid-total preview and the invoice
// pricing engine (BL-2032) all price through it, in Money (ADR 060 — Money Type).

import { Money } from '../money/money';
import type { Rate } from '../rate/rate';
import type { PremiumSchedule, PremiumTier } from './schedule';

/** Which bound, if any, moved the premium. */
export type PremiumBound = 'floor' | 'cap' | null;

/**
 * Applies the schedule's floor and cap, once, after the tiers.
 *
 * The floor applies only when the price is above zero (a free lot never incurs
 * a minimum fee). Inversion guard: when an operator has saved `minFee > maxFee`,
 * the effective floor is clamped to the cap, so the cap — which exists to
 * protect the buyer — is never breached.
 */
function applyBounds(premium: Money, price: Money, schedule: PremiumSchedule): { premium: Money; bound: PremiumBound } {
  const { minFee, maxFee } = schedule;
  let result = premium;
  let bound: PremiumBound = null;
  if (maxFee != null && result.compare(maxFee) > 0) {
    result = maxFee;
    bound = 'cap';
  }
  if (minFee != null && price.compare(zeroOf(price)) > 0) {
    const floor = maxFee != null && minFee.compare(maxFee) > 0 ? maxFee : minFee;
    if (result.compare(floor) < 0) {
      result = floor;
      bound = 'floor';
    }
  }
  return { premium: result, bound };
}

function zeroOf(m: Money): Money {
  return Money.of('0', m.currency);
}

/** Tiers ascending by threshold, the unbounded tier (`null`) last. */
function sortTiers(tiers: readonly PremiumTier[]): PremiumTier[] {
  return [...tiers].sort((a, b) => {
    if (a.threshold == null) return b.threshold == null ? 0 : 1;
    if (b.threshold == null) return -1;
    return a.threshold.compare(b.threshold);
  });
}

/**
 * The tier a price lands in, with the threshold below it: the lowest tier whose
 * threshold covers the price (thresholds are inclusive), else the top tier —
 * which defends a hand-built all-finite schedule whose ceiling sits below the price.
 */
function landingTier(price: Money, tiers: readonly PremiumTier[]): { tier: PremiumTier; from: Money } {
  const sorted = sortTiers(tiers);
  const index = sorted.findIndex((t) => t.threshold == null || price.compare(t.threshold) <= 0);
  const at = index === -1 ? sorted.length - 1 : index;
  const below = at > 0 ? sorted[at - 1].threshold : null;
  return { tier: sorted[at], from: below ?? zeroOf(price) };
}

/** One line of the premium's working: `rate` of `base`, which is `amount`. */
export interface PremiumBreakdownRow {
  /** The band's lower edge (exclusive); zero for the first band, a flat rate, or a free price. */
  from: Money;
  /** The band's upper edge (inclusive), or `null` when the band has no ceiling. */
  upTo: Money | null;
  rate: Rate;
  /** The part of the price this line charges. */
  base: Money;
  /** `base` × `rate`, rounded half-up to the minor unit. */
  amount: Money;
}

/** The premium's working, line by line, and what the floor or cap made of it. */
export interface PremiumBreakdown {
  rows: PremiumBreakdownRow[];
  /** The sum of the rows, before the floor and cap. */
  raw: Money;
  /** The premium charged: `raw` after the floor and cap. Always `calculateBuyersPremium`'s answer. */
  premium: Money;
  /** The bound that moved the premium, or `null`. */
  bound: PremiumBound;
}

/**
 * Marginal: each tier's rate applies only to its slice of the price, each slice
 * rounded to the minor unit before summing. Price above the last finite
 * threshold is not charged (there is no tier covering it).
 */
function marginalRows(price: Money, tiers: readonly PremiumTier[]): PremiumBreakdownRow[] {
  const rows: PremiumBreakdownRow[] = [];
  let floor = zeroOf(price);
  for (const tier of sortTiers(tiers)) {
    const ceiling = tier.threshold == null || price.compare(tier.threshold) < 0 ? price : tier.threshold;
    const slice = ceiling.minus(floor);
    if (slice.compare(zeroOf(price)) > 0) {
      rows.push({ from: floor, upTo: tier.threshold, rate: tier.rate, base: slice, amount: slice.times(tier.rate) });
    }
    if (tier.threshold == null || price.compare(tier.threshold) <= 0) break;
    floor = tier.threshold;
  }
  return rows;
}

/** The lines a schedule charges on a positive price, before the floor and cap. */
function breakdownRows(price: Money, schedule: PremiumSchedule): PremiumBreakdownRow[] {
  if (schedule.type === 'Flat') {
    return [{ from: zeroOf(price), upTo: null, rate: schedule.rate, base: price, amount: price.times(schedule.rate) }];
  }
  if (schedule.tiers.length === 0) return [];
  if (schedule.combineMode === 'Bracket') {
    const { tier, from } = landingTier(price, schedule.tiers);
    return [{ from, upTo: tier.threshold, rate: tier.rate, base: price, amount: price.times(tier.rate) }];
  }
  return marginalRows(price, schedule.tiers);
}

/**
 * The buyer's premium on one lot's price, line by line (BL-2989 — "Try the rule").
 *
 * Flat charges its rate on the whole price: one line. Tiered combines its tiers by
 * `combineMode`: Marginal lists each tier on its slice, Bracket lists the one tier
 * the price lands in, on the whole price (thresholds inclusive). Floor and cap
 * apply once, after the lines. Products round half-up to the currency's minor unit.
 * {@link calculateBuyersPremium} is this function's `premium`, so the working and
 * the charge are one computation (ADR 010 — Isomorphic Services).
 *
 * @param price - The lot's price (hammer, or a re-priced hammer per CR10 (a)).
 * @param schedule - The resolved schedule, or `null` when the lot carries none.
 * @returns The lines, their sum, the premium after the bounds, and the bound applied.
 *   No lines and a zero premium for no schedule or a price of zero or less.
 * @throws CurrencyMismatchError when the schedule's dollar figures are in another currency.
 */
export function premiumBreakdown(price: Money, schedule: PremiumSchedule | null): PremiumBreakdown {
  const zero = zeroOf(price);
  if (schedule == null || price.compare(zero) <= 0) return { rows: [], raw: zero, premium: zero, bound: null };
  const rows = breakdownRows(price, schedule);
  const raw = rows.reduce((sum, row) => sum.plus(row.amount), zero);
  if (rows.length === 0) return { rows, raw, premium: zero, bound: null };
  return { rows, raw, ...applyBounds(raw, price, schedule) };
}

/**
 * The buyer's premium on one lot's price: {@link premiumBreakdown}'s `premium`.
 *
 * @param price - The lot's price (hammer, or a re-priced hammer per CR10 (a)).
 * @param schedule - The resolved schedule, or `null` when the lot carries none.
 * @returns The premium, in the price's currency; zero for no schedule or a
 *   price of zero or less.
 * @throws CurrencyMismatchError when the schedule's dollar figures are in
 *   another currency.
 */
export function calculateBuyersPremium(price: Money, schedule: PremiumSchedule | null): Money {
  return premiumBreakdown(price, schedule).premium;
}

/**
 * The headline rate at a price: the flat rate, or the rate of the tier the price
 * lands in (the Bracket rate, and the marginal rate at that price). The one
 * place "which tier does this price land in" is decided (ADR 010 § 5).
 *
 * @param price - The price to read the rate at.
 * @param schedule - The resolved schedule, or `null`.
 * @returns The rate, or `null` when the schedule charges nothing.
 */
export function premiumRateAt(price: Money, schedule: PremiumSchedule | null): Rate | null {
  if (schedule == null) return null;
  if (schedule.type === 'Flat') return schedule.rate;
  if (schedule.tiers.length === 0) return null;
  return landingTier(price, schedule.tiers).tier.rate;
}
